/**
 * ═══════════════════════════════════════════════════════════════════════════
 * UN VEHÍCULO DE FLOTA, CON UNA SOLA FORMA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── EL PROBLEMA, EN UNA FRASE ────────────────────────────────────────────
 *
 * Había DOS formularios de alta escribiendo NOMBRES DE CAMPO DISTINTOS para la
 * misma cosa, y cada pantalla leía los del otro:
 *
 *   · Flota (`(tabs)/fleet.tsx`) escribía `make`, `year`, `carImage`,
 *     `acWorking`, `aestheticOk` — y NO escribía `plate`.
 *   · El panel (`(tabs)/dashboard.tsx`) escribía `brand`, `plate`, `status` —
 *     y NO escribía `make`, ni `year`, ni `carImage`. De regalo, un campo `id`
 *     inventado con `Math.random()`.
 *
 * Y los lectores:
 *   · El Marketplace pinta `car.make`, `car.year` y `car.carImage`.
 *   · La tabla del panel pinta `v.plate` y `v.brand`.
 *
 * O sea que estaba roto EN LAS DOS DIRECCIONES, no en una: el carro dado de
 * alta por el panel salía en la vitrina sin nombre, sin año y con una foto de
 * relleno de un host externo; y el carro dado de alta por Flota salía en la
 * tabla del panel sin marca y sin placa. El buscador del Marketplace filtra por
 * `make`, `model` y `year`: del primero solo encontraba `model`.
 *
 * El dueño nunca podía enterarse, porque cada pantalla le enseñaba el carro que
 * ELLA había escrito bien.
 *
 * ── POR QUÉ UN NORMALIZADOR Y NO SOLO «ARREGLAR EL FORMULARIO» ───────────
 *
 * Porque los documentos rotos YA ESTÁN EN LA BASE. Arreglar solo la escritura
 * deja a todos los carros dados de alta hasta hoy saliendo sin nombre para
 * siempre. `leerVehiculo` los repara al leerlos, sin migración y sin tocar un
 * solo documento.
 *
 * `paraGuardar` hace lo propio del otro lado: cualquiera que llame a
 * `addVehicle` acaba con la MISMA forma, aunque mande `brand`. Las dos mitades
 * hacen falta.
 */

export type VehiculoDeFlota = {
  id: string;
  /** La marca. Canónico: `make`. `brand` es el alias que escribía el panel. */
  make: string;
  model: string;
  year: number | null;
  plate: string;
  price: number;
  carImage: string | null;
  status: string;
  isPublished: boolean;
  acWorking: boolean;
  aestheticOk: boolean;
  requiresSlotPayment: boolean;
  /**
   * Veredicto del equipo sobre este carro: 'PENDIENTE' | 'APROBADO' |
   * 'RECHAZADO', o `null` para los que se crearon ANTES de que existiera la
   * revisión. Ese `null` no es «aprobado»: es «de antes», y por eso en la
   * vitrina no lleva sello. Ver functions/src/drivers.ts.
   */
  revision: string | null;
  motivoRechazo: string | null;
  /** El documento tal cual vino, por si alguna pantalla necesita algo más. */
  crudo: any;
};

const texto = (...valores: unknown[]): string => {
  for (const v of valores) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
};

const numero = (...valores: unknown[]): number | null => {
  for (const v of valores) {
    const n = Number(v);
    if (Number.isFinite(n) && n !== 0) return n;
  }
  return null;
};

/**
 * Lee un documento de `/vehicles` venga de donde venga.
 *
 * Nunca devuelve `undefined` en un campo de texto: una tarjeta con el título en
 * blanco es justo lo que esto viene a cerrar. Cuando de verdad no hay marca ni
 * modelo, `titulo` dice «Vehículo sin nombre», que al menos es visible y se
 * puede buscar en el panel para arreglarlo.
 */
export function leerVehiculo(doc: any): VehiculoDeFlota & { titulo: string } {
  const make = texto(doc?.make, doc?.brand, doc?.marca);
  const model = texto(doc?.model, doc?.modelo);
  const nombre = `${make} ${model}`.trim();
  return {
    id: String(doc?.id ?? ''),
    make,
    model,
    year: numero(doc?.year, doc?.anio, doc?.['año']),
    plate: texto(doc?.plate, doc?.placa, doc?.licensePlate),
    price: Number(doc?.price) || 0,
    carImage: texto(doc?.carImage, doc?.image, doc?.imageUrl, doc?.foto) || null,
    status: texto(doc?.status, doc?.estado) || 'Disponible',
    isPublished: doc?.isPublished === true,
    acWorking: doc?.acWorking === true,
    aestheticOk: doc?.aestheticOk === true,
    requiresSlotPayment: doc?.requiresSlotPayment === true,
    revision: texto(doc?.revision) || null,
    motivoRechazo: texto(doc?.motivoRechazo) || null,
    titulo: nombre || 'Vehículo sin nombre',
    crudo: doc,
  };
}

/**
 * Deja los datos de un alta en la forma canónica, vengan como vengan.
 *
 * ⚠️ Quita `id` a propósito. El panel metía `id: Math.random().toString()` en
 * el cuerpo del documento: un campo que no es el id real (ese lo pone
 * Firestore), que no sirve para nada y que confunde a cualquiera que lo lea
 * creyendo que identifica algo.
 */
export function paraGuardar(datos: any): Record<string, any> {
  const { id, brand, marca, modelo, placa, estado, anio, ...resto } = datos ?? {};
  const v = leerVehiculo(datos);
  return {
    ...resto,
    make: v.make,
    model: v.model,
    year: v.year,
    plate: v.plate,
    price: v.price,
    carImage: v.carImage,
    status: v.status,
    acWorking: v.acWorking,
    aestheticOk: v.aestheticOk,
  };
}
